import type { ReactNode } from 'react'
import { useCallback, useState } from 'react'
import {
  ModalsManagerConfirmModal,
  type ModalsManagerOpenConfirmModalProps,
} from './confirmModal'
import { ModalContext } from './useModals'
import type { ModalsManagerOpenModalProps } from './openModal'
import { ModalsManagerModal } from './openModal'
import {
  ModalsManagerDialogOrDrawer,
  type ModalsManagerDialogOrDrawerProps,
} from './openDialogOrDrawerModal'

type Props = {
  children: ReactNode
}

export type ModalManagerModalType =
  | {
      type: 'confirm'
      modalId: string
      modal: ModalsManagerOpenConfirmModalProps
    }
  | {
      type: 'modal'
      modalId: string
      modal: ModalsManagerOpenModalProps
    }
  | {
      type: 'dialogOrDrawer'
      modalId: string
      modal: ModalsManagerDialogOrDrawerProps
    }

export const ModalManager = ({ children }: Props) => {
  const [modals, setModals] = useState<ModalManagerModalType[]>([])

  const openModal = useCallback((data: ModalManagerModalType) => {
    const newModalData = data

    setModals((previous) => [...previous, newModalData])
  }, [])

  const updateModal = useCallback(
    (modalId: string, newData: Partial<ModalManagerModalType['modal']>) => {
      setModals((currentModals) =>
        currentModals.map((modal) => {
          if (modal.modalId === modalId) {
            const newModal = modal

            newModal.modal = { ...modal.modal, ...newData }

            // Use lodash's merge to perform a deep merge
            return newModal
          }
          return modal
        }),
      )
    },
    [], // No need for modals in dependency array
  )

  const closeModals = () => {
    setModals([])
  }

  const displayModals = () => {
    if (modals.length > 0) {
      return modals.map((modal, index) => {
        const zIndex = 2000 + index * 5

        if (modal.type === 'confirm') {
          return (
            <ModalsManagerConfirmModal
              key={modal.modalId}
              modalId={modal.modalId}
              zIndex={zIndex}
              {...modal.modal}
            />
          )
        }

        if (modal.type === 'modal') {
          return (
            <ModalsManagerModal
              key={modal.modalId}
              modalId={modal.modalId}
              zIndex={zIndex}
              {...modal.modal}
            />
          )
        }

        if (modal.type === 'dialogOrDrawer') {
          return (
            <ModalsManagerDialogOrDrawer
              key={modal.modalId}
              modalId={modal.modalId}
              zIndex={zIndex}
              {...modal.modal}
            />
          )
        }

        return null
      })
    }

    return null
  }

  return (
    <ModalContext.Provider
      value={{
        closeModals,
        openModal,
        updateModal,
      }}
    >
      {displayModals()}
      {children}
    </ModalContext.Provider>
  )
}
